'use client'

import { FormEventHandler, useEffect, useRef } from 'react'
import { Button, Dialog, Divider, InfoBanner, Navigation, Note, Text } from '@vinted/web-ui'
import { X24 } from '@vinted/monochrome-icons'

import { useBrowserNavigation } from '@marketplace-web/browser/browser-navigation-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session-data'

import { getHomepageSessionIdFromUrl } from '@marketplace-web/home/home-page-util'
import { getSearchSessionData } from '@marketplace-web/search/search-feature'
import { CurrencyAmountModel } from '@marketplace-web/currency/currency-data'
import {
  buyerWantItemEvent,
  openOfferScreenEvent,
  viewScreenEvent,
  startMakeOfferRequestEvent,
  OfferRequestPriceSuggestionModel,
  OfferPriceSuggestionOption,
} from '@marketplace-web/offers/price-offers-data'

import { SERVICE_FEE_HELP_URL } from '../../constants/routes'
import OfferSubmitButton from '../OfferSubmitButton/OfferSubmitButton'
import { OfferPriceField } from '../fields/OfferPriceField'
import OfferPriceWithSuggetionsField from '../fields/OfferPriceWithSuggetionsField'
import ItemSummary from '../ItemSummary/ItemSummary'

type Props = {
  maxDiscount?: string
  transactionId?: number
  remainingOffers?: number
  offerPriceSuggestions?: Array<OfferRequestPriceSuggestionModel> | null
  show: boolean
  minItemPrice?: CurrencyAmountModel
  maxItemPrice?: CurrencyAmountModel
  thresholdPrice: string | null
  isSubmitting: boolean
  itemPrice: CurrencyAmountModel
  onSubmit: FormEventHandler
  onClose: () => void
  itemId: number
  itemTitle?: string
  itemThumbnailUrl?: string | null
  orderItemIds?: Array<number>
  offerRequestSessionId?: string
  selectedOfferRequestPriceSuggestion?: OfferPriceSuggestionOption
  onSelectOfferRequestPriceSuggestion?: (offerPriceSuggestion: OfferPriceSuggestionOption) => void
}

const BuyerOfferRequestDialog = ({
  show,
  onClose,
  onSubmit,
  itemPrice,
  maxDiscount,
  isSubmitting,
  minItemPrice,
  maxItemPrice,
  transactionId,
  thresholdPrice,
  remainingOffers,
  offerPriceSuggestions,
  itemId,
  itemTitle,
  itemThumbnailUrl,
  orderItemIds,
  offerRequestSessionId,
  selectedOfferRequestPriceSuggestion,
  onSelectOfferRequestPriceSuggestion,
}: Props) => {
  const { track } = useTracking()
  const translate = useTranslate('conversation.hero_message.make_offer')

  const isOfferRequestPriceSuggestionEnabled = !!offerPriceSuggestions
  const thresholdBannerKey = 'offer_modal.threshold_banner_title.buyer'

  const { screen } = useSession()
  const { searchParams } = useBrowserNavigation()
  const referrerScreen = searchParams.referrer?.toString()
  const searchSessionData = getSearchSessionData()
  const searchSessionIdRef = useRef(
    referrerScreen === 'catalog' ? searchSessionData.searchSessionId : null,
  )

  useEffect(() => {
    if (!show) return

    track(viewScreenEvent({ screen: 'buyer_originating_offer' }))
  }, [show, track])

  useEffect(() => {
    if (!show || !offerRequestSessionId || !transactionId) return

    track(
      startMakeOfferRequestEvent({
        offerRequestSessionId,
        currentPrice: Number(itemPrice.amount),
        hasSuggestedPrices: isOfferRequestPriceSuggestionEnabled,
        transactionId: transactionId.toString(),
      }),
    )

    track(
      buyerWantItemEvent({
        itemId,
        transactionId,
        button: 'make_offer',
        globalSearchSessionId: searchSessionData.globalSearchSessionId,
        globalCatalogBrowseSessionId: searchSessionData.globalCatalogBrowseSessionId,
        searchSessionId: searchSessionIdRef.current,
        homepageSessionId: getHomepageSessionIdFromUrl(),
      }),
    )
  }, [
    isOfferRequestPriceSuggestionEnabled,
    itemPrice.amount,
    offerRequestSessionId,
    show,
    track,
    transactionId,
    itemId,
    itemPrice,
    searchSessionData,
  ])

  useEffect(() => {
    if (screen !== 'item' || !transactionId || !show) return

    track(
      openOfferScreenEvent({
        isBuyer: true,
        currentPrice: Number(itemPrice.amount),
        currencyCode: itemPrice.currencyCode,
        openedFrom: 'item_page',
        transactionId,
      }),
    )
  }, [screen, itemPrice, transactionId, show, track])

  function renderNavigation() {
    return (
      <Navigation
        body={translate('offer_modal.title')}
        right={
          <Button
            inline
            styling="flat"
            iconName={X24}
            testId="offer-modal-navigation-close-button"
            onClick={onClose}
          />
        }
        testId="offer-modal-navigation"
      />
    )
  }

  function renderRemainingOffersNote() {
    if (!remainingOffers) return null

    return <Note text={translate('offer_modal.caption', { remaining_offers: remainingOffers })} />
  }

  return (
    <Dialog show={show} testId="offer-modal">
      <form onSubmit={onSubmit}>
        {renderNavigation()}
        <Divider />
        {thresholdPrice && !isOfferRequestPriceSuggestionEnabled && (
          <InfoBanner
            body={
              <Text
                as="span"
                text={translate(thresholdBannerKey, {
                  amount: thresholdPrice,
                  'learn-more': chunks => (
                    <a href={SERVICE_FEE_HELP_URL} target="_blank" rel="noreferrer">
                      {chunks}
                    </a>
                  ),
                })}
                html
              />
            }
            type="info"
          />
        )}
        <ItemSummary
          price={itemPrice}
          title={itemTitle}
          thumbnailUrl={itemThumbnailUrl}
          itemCount={orderItemIds?.length}
        />
        {isOfferRequestPriceSuggestionEnabled ? (
          <OfferPriceWithSuggetionsField
            maxDiscount={maxDiscount}
            minItemPrice={minItemPrice}
            maxItemPrice={maxItemPrice}
            currentItemPrice={itemPrice}
            transactionId={transactionId}
            offerPriceSuggestions={offerPriceSuggestions}
            itemId={itemId}
            orderItemIds={orderItemIds}
            offerRequestSessionId={offerRequestSessionId}
            selectedOfferRequestPriceSuggestion={selectedOfferRequestPriceSuggestion}
            onSelectOfferRequestPriceSuggestion={onSelectOfferRequestPriceSuggestion}
          />
        ) : (
          <OfferPriceField
            maxDiscount={maxDiscount}
            minItemPrice={minItemPrice}
            maxItemPrice={maxItemPrice}
            currentItemPrice={itemPrice}
            itemId={itemId}
            orderItemIds={orderItemIds}
          />
        )}
        {thresholdPrice && isOfferRequestPriceSuggestionEnabled && (
          <InfoBanner
            body={
              <Text
                as="span"
                text={translate(thresholdBannerKey, {
                  amount: thresholdPrice,
                  'learn-more': chunks => (
                    <a href={SERVICE_FEE_HELP_URL} target="_blank" rel="noreferrer">
                      {chunks}
                    </a>
                  ),
                })}
                html
              />
            }
            type="info"
          />
        )}
        {renderRemainingOffersNote()}
        <OfferSubmitButton
          currency={itemPrice.currencyCode}
          isSubmitting={isSubmitting}
          isOfferPriceDisplayEnabled={isOfferRequestPriceSuggestionEnabled}
          transactionId={transactionId}
        />
      </form>
    </Dialog>
  )
}

export default BuyerOfferRequestDialog
